import React, { useState, useEffect } from 'react';

const Episodes = () => {
  const [selectedFilters, setSelectedFilters] = useState({
    guestType: 'All',
    topic: 'All', 
    duration: 'All',
    sortBy: 'Newest'
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [featuredEpisode, setFeaturedEpisode] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(''); // 'success', 'error', 'loading', ''
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterName) {
      setNewsletterStatus('error');
      setNewsletterMessage('Molimo vas unesite ime i email adresu.');
      return;
    }

    setNewsletterStatus('loading');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newsletterName,
          email: newsletterEmail,
          source: 'homepage'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Nastala je greška. Pokušajte ponovo.');
      }

      setNewsletterStatus('success');
      setNewsletterMessage('Uspješno ste se pretplatili!');
      setNewsletterEmail('');
      setNewsletterName('');
      
      setTimeout(() => {
        setNewsletterStatus('');
        setNewsletterMessage('');
      }, 3000);
    } catch (error) {
      setNewsletterStatus('error');
      setNewsletterMessage(error.message);
    }
  };

  useEffect(() => {
    fetchEpisodes();
  }, []);

  const fetchEpisodes = async () => {
    try {
      // Fetch featured episode
      const featuredResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/episodes/featured/public`);
      if (featuredResponse.ok) {
        const featuredData = await featuredResponse.json();
        setFeaturedEpisode(featuredData);
      }

      // Fetch all episodes
      const episodesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/episodes/public`);
      if (episodesResponse.ok) {
        const episodesData = await episodesResponse.json();
        // Filter out the featured episode from the list to avoid duplication
        const nonFeaturedEpisodes = episodesData.items.filter(ep => !(ep.is_featured || ep.isFeatured));
        setEpisodes(nonFeaturedEpisodes);
      }
    } catch (error) {
      console.error('Error fetching episodes:', error);
      // Set empty arrays as fallback
      setFeaturedEpisode(null);
      setEpisodes([]);
    } finally {
      setLoading(false);
    }
  };

  const filterOptions = {
    guestType: ['All', 'Founder', 'Creator', 'Investor', 'Artist', 'Coach'],
    topic: ['All', 'Failure', 'Scaling', 'Mental Health', 'Branding', 'Team Building'],
    duration: ['All', '<30 min', '30-60 min', '>1hr'],
    sortBy: ['Newest', 'Oldest', 'Most Watched']
  };

  const PlayIcon = () => (
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8 5v14l11-7z"/>
    </svg>
  );

  const BookmarkIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
    </svg>
  );

  return (
    <div className="min-h-screen bg-midnight text-warm-white font-geist">
      {/* Section Header */}
      <div className="pt-32 pb-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-clash text-5xl lg:text-7xl font-bold mb-6 tracking-tight">
            Epizode
          </h1>
          <p className="text-xl lg:text-2xl text-soft-gray max-w-3xl mx-auto leading-relaxed font-geist">
            Sirovi razgovori sa vizionarskim umovima. Svaka epizoda djeluje kao priča vrijedna otkrivanja, a ne samo sadržaj.
          </p>
        </div>
      </div>

      {/* Featured Episode */}
      <div className="px-6 lg:px-12 mb-20">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-electric-yellow text-lg">Učitava izdvojenu epizodu...</div>
            </div>
          ) : featuredEpisode ? (
            <div className="bg-gradient-to-r from-charcoal/80 to-deep-teal/20 rounded-3xl overflow-hidden border border-slate-dark/20 hover:scale-[1.01] transition-transform duration-500 group">
              <div className="flex flex-col lg:flex-row">
                {/* Right Side - Content (moved first for mobile-first approach) */}
                <div className="order-2 lg:order-1 p-8 lg:p-12 flex-1 flex flex-col justify-center">
                  {/* Duration */}
                  <div className="flex items-center justify-center mb-4">
                    <span className="bg-deep-teal/30 text-electric-yellow px-4 py-2 rounded-full text-sm font-bold">
                      {featuredEpisode.duration}
                    </span>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-electric-yellow font-bold text-sm uppercase tracking-widest mb-2 text-center">
                      {featuredEpisode.guest_role || featuredEpisode.guestRole}
                    </h3>
                    <h2 className="font-clash text-3xl lg:text-4xl font-bold leading-tight mb-4">
                      {featuredEpisode.title}
                    </h2>
                  </div>

                  <blockquote className="text-soft-gray text-lg lg:text-xl leading-relaxed mb-6 italic font-geist">
                    {featuredEpisode.description}
                  </blockquote>

                  {/* Listen Buttons */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <a
                      href={featuredEpisode.spotify_url || featuredEpisode.spotifyUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300 flex items-center justify-center gap-2"
                    >
                      <span>🎧</span> Spotify
                    </a>
                    <a
                      href={featuredEpisode.youtube_url || featuredEpisode.youtubeUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-transparent border-2 border-soft-gray text-soft-gray px-6 py-3 rounded-xl font-bold hover:bg-soft-gray hover:text-midnight transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <span>▶️</span> YouTube
                    </a>
                  </div>
                </div>

                {/* Left Side - Image */}
                <div className="order-1 lg:order-2 relative overflow-hidden lg:w-96 lg:flex-shrink-0">
                  <img
                    src={featuredEpisode.thumbnail}
                    alt="Featured Episode"
                    className="w-full h-64 lg:h-full lg:min-h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute top-6 left-6">
                    <span className="bg-electric-yellow text-midnight px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide">
                      Izdvojeno
                    </span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <a 
                      href={featuredEpisode.youtube_url || featuredEpisode.youtubeUrl || '#'}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-electric-yellow text-midnight p-4 rounded-full hover:scale-110 transition-transform duration-300"
                    >
                      <PlayIcon />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="text-soft-gray text-xl">Nema dostupnih izdvojenih epizoda u ovom trenutku.</div>
              <p className="text-soft-gray mt-4">Vratite se kasnije za nevjerovatne razgovore!</p>
            </div>
          )}
        </div>
      </div>

      {/* Episodes Grid */}
      <div className="px-6 lg:px-12 pb-20">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-electric-yellow text-lg">Učitava epizode...</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {episodes.length === 0 ? (
                <div className="col-span-full text-center py-20">
                  <div className="text-soft-gray text-xl">Nema dostupnih epizoda u ovom trenutku.</div>
                  <p className="text-soft-gray mt-4">Vratite se kasnije za nevjerovatne razgovore!</p>
                </div>
              ) : (
                episodes.map((episode) => (
                  <div
                    key={episode.id}
                    className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl overflow-hidden hover:scale-[1.02] hover:border-electric-yellow/20 transition-all duration-500 group cursor-pointer"
                  >
                    {/* Episode Thumbnail */}
                    <div className="relative overflow-hidden">
                      <img
                        src={episode.thumbnail}
                        alt={episode.title}
                        className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-midnight/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      {/* Episode Number */}
                      <div className="absolute top-4 left-4">
                        <span className="bg-midnight/80 backdrop-blur-sm text-electric-yellow px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                          {episode.episode_number || episode.episodeNumber}
                        </span>
                      </div>

                      {/* Hover Controls */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                        <a 
                          href={episode.youtube_url || episode.youtubeUrl || '#'}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="bg-electric-yellow text-midnight p-3 rounded-full hover:scale-110 transition-transform duration-300"
                        >
                          <PlayIcon />
                        </a>
                      </div>
                    </div>

                    {/* Episode Content */}
                    <div className="p-6">
                      {/* Guest Info */}
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={episode.guest_photo || episode.guestPhoto}
                          alt={episode.guest_name || episode.guestName}
                          className="w-12 h-12 rounded-full object-cover border-2 border-electric-yellow/20"
                        />
                        <div className="text-left">
                          <h4 className="font-bold text-warm-white text-left">{episode.guest_name || episode.guestName}</h4>
                          <p className="text-soft-gray text-sm text-left">{episode.guest_role || episode.guestRole}</p>
                        </div>
                      </div>

                      {/* Episode Title */}
                      <a 
                        href={episode.youtube_url || episode.youtubeUrl || '#'}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <h3 className="font-clash text-xl font-bold leading-tight mb-4 group-hover:text-electric-yellow transition-colors duration-300 text-left hover:text-electric-yellow cursor-pointer">
                          {episode.title}
                        </h3>
                      </a>

                      {/* Duration */}
                      <div className="flex items-start justify-start mb-4">
                        <span className="bg-deep-teal/20 text-electric-yellow px-3 py-1 rounded-full text-sm font-medium">
                          {episode.duration}
                        </span>
                      </div>

                      {/* CTA */}
                      <a 
                        href={episode.youtube_url || episode.youtubeUrl || '#'}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block w-full bg-transparent border border-electric-yellow/30 text-electric-yellow px-4 py-3 rounded-xl font-bold hover:bg-electric-yellow hover:text-midnight transition-all duration-300 text-center"
                      >
                        Pusti sada
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Load More Button - Only show if there are episodes */}
          {!loading && episodes.length > 0 && (
            <div className="text-center mt-12">
              <button className="bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold text-lg hover:bg-electric-yellow/90 hover:scale-105 transition-all duration-300">
                Učitaj više epizoda
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="px-6 lg:px-12 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-deep-teal/20 to-electric-yellow/10 backdrop-blur-sm border border-electric-yellow/20 rounded-3xl p-12">
            <h2 className="font-clash text-4xl lg:text-5xl font-bold mb-6">
              Ne propustite šta slijedi
            </h2>
            <p className="text-xl text-soft-gray mb-8">
              Pretplatite se na nedjeljna izdanja sirovih, nefiltriranih razgovora koji su važni.
            </p>
            
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
              <input
                type="text"
                placeholder="Vaše ime"
                value={newsletterName}
                onChange={(e) => setNewsletterName(e.target.value)}
                required
                className="flex-1 bg-midnight/80 border border-slate-dark rounded-xl px-6 py-4 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
              />
              <input
                type="email"
                placeholder="Vaša email adresa"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                className="flex-1 bg-midnight/80 border border-slate-dark rounded-xl px-6 py-4 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:border-electric-yellow/50 transition-all duration-300"
              />
              <button 
                type="submit"
                disabled={newsletterStatus === 'loading'}
                className="bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold hover:bg-electric-yellow/90 hover:scale-105 transition-all duration-300 whitespace-nowrap disabled:opacity-70 disabled:hover:scale-100"
              >
                {newsletterStatus === 'loading' ? 'Slanje...' : 'Pretplati se'}
              </button>
            </form>
            
            {newsletterMessage && (
              <div className={`mt-6 p-4 rounded-xl max-w-2xl mx-auto border ${
                newsletterStatus === 'success' 
                  ? 'bg-deep-teal/20 border-deep-teal text-deep-teal' 
                  : 'bg-red-500/20 border-red-500 text-red-400'
              }`}>
                {newsletterMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Episodes;